import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Ad from "src/module/Ad";
import styled from "styled-components";

import WideDefaultLayout from "@layouts/WideDefaultLayout";

import { authAtom } from "@core/atoms/auth.atom";
import {
  showDailyTodoSortFormAtom,
  showGridFormAtom,
  showSortFormAtom,
  showWideAtom,
  todoServerAtom,
  viewModeAtom,
} from "@core/atoms/todo.atom";
import {
  useCheckServerTodo,
  useToggleServerTodoEnabled,
} from "@core/hooks/mutations/todo";
import useCharacters from "@core/hooks/queries/character/useCharacters";
import { useServerTodos } from "@core/hooks/queries/todo";
import useModalState from "@core/hooks/useModalState";
import { usePersistedGridConfig } from "@core/hooks/usePersistedGridConfig";
import type { ServerName } from "@core/types/lostark";
import { getServerCounts, getTodayWeekday } from "@core/utils";
import queryKeyGenerator from "@core/utils/queryKeyGenerator";

import Button from "@components/Button";
import Dial from "@components/Dial";
import Modal from "@components/Modal";
import SortCharacters from "@components/SortCharacters";
import TestDataNotify from "@components/TestDataNotify";
import DailyTodoSortModal from "@components/todo/DailyTodoSortModal";
import Profit from "@components/todo/Profit";
import ServerTodos from "@components/todo/ServerTodos";
import TodoList from "@components/todo/TodoList";
import GridConfigPanel from "@components/todo/TodoList/GridConfigPanel";
import TodoTable from "@components/todo/TodoTable";
import UncheckedSummaryModal from "@components/todo/UncheckedSummaryModal";

interface ServerTodoSummaryItem {
  todoId: number;
  name: string;
  checked: boolean;
  serverName: ServerName;
  hasState: boolean;
}

interface ServerTodoSummaryItem {
  todoId: number;
  name: string;
  checked: boolean;
  serverName: ServerName;
  hasState: boolean;
}

// 하단 네비(max1024 media query)와 일치시켜야 900~1024 구간에서
// 토글만 숨고 로직은 허용되는 불일치가 생기지 않음
const TABLE_VIEW_MIN_WIDTH = 1024;

const TodoIndex = () => {
  const queryClient = useQueryClient();
  const [todoServer, setTodoServer] = useAtom(todoServerAtom);
  const [viewMode, setViewMode] = useAtom(viewModeAtom);
  const [gridConfig, setGridConfig] = usePersistedGridConfig();
  const [summaryModal, setSummaryModal] = useState(false);
  const [serverTodoModal, setServerTodoModal] = useModalState<boolean>();
  const [expandedServers, setExpandedServers] = useState<Set<string>>(new Set());
  const showSortForm = useAtomValue(showSortFormAtom);
  const showGridForm = useAtomValue(showGridFormAtom);
  const showDailyTodoSortForm = useAtomValue(showDailyTodoSortFormAtom);
  const showWide = useAtomValue(showWideAtom);
  const auth = useAtomValue(authAtom);
  const getCharacters = useCharacters();

  // 반응형: 뷰포트가 좁으면 표 뷰 비활성화, 카드 뷰로 자동 fallback
  const [isTableViewAllowed, setIsTableViewAllowed] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= TABLE_VIEW_MIN_WIDTH;
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handleResize = () => {
      setIsTableViewAllowed(window.innerWidth >= TABLE_VIEW_MIN_WIDTH);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const effectiveViewMode = isTableViewAllowed ? viewMode : "card";

  const shouldShowAd = !auth.adsDate || new Date(auth.adsDate) <= new Date();


  const visibleCharacters = useMemo(() => {
    return (getCharacters.data || []).filter(
      (character) => character.settings.showCharacter
    );
  }, [getCharacters.data]);

  const characters = useMemo(() => {
    return visibleCharacters.filter((character) =>
      todoServer === "전체" ? true : character.serverName === todoServer
    );
  }, [visibleCharacters, todoServer]);

  const serverCounts = useMemo(() => {
    return getServerCounts(visibleCharacters);
  }, [visibleCharacters]);

  const servers = Object.keys(serverCounts) as ServerName[];
  const visibleServers = useMemo(() => {
    const serverSet = new Set<ServerName>();
    characters.forEach((character) => {
      serverSet.add(character.serverName);
    });
    return Array.from(serverSet) as ServerName[];
  }, [characters]);
  const showServerButtons = servers.length > 0;
  const serverTodosOverview = useServerTodos(undefined, {
    enabled: servers.length > 0,
  });
  const todayWeekday = getTodayWeekday();
  const serverTodoQueryKey = queryKeyGenerator.getServerTodos(undefined);

  const toggleServerTodoEnabledMutation = useToggleServerTodoEnabled({
    onSuccess: (overview) => {
      queryClient.setQueryData(serverTodoQueryKey, overview);
    },
    onError: () => {
      toast.error("원정대 숙제 상태를 초기화하지 못했습니다.");
    },
  });

  const checkServerTodoMutation = useCheckServerTodo({
    onSuccess: (overview) => {
      queryClient.setQueryData(serverTodoQueryKey, overview);
    },
    onError: () => {
      toast.error("원정대 숙제를 체크하지 못했습니다.");
    },
  });

  const isServerTodoUpdating =
    toggleServerTodoEnabledMutation.isPending ||
    checkServerTodoMutation.isPending;

  const serverTodoSummaries = useMemo(() => {
    if (!serverTodosOverview.data) {
      return {} as Partial<Record<ServerName, ServerTodoSummaryItem[]>>;
    }

    const { todos, states } = serverTodosOverview.data;
    const stateMap = states.reduce<Record<string, (typeof states)[number]>>(
      (acc, state) => {
        acc[`${state.serverName}-${state.todoId}`] = state;
        return acc;
      },
      {}
    );

    const summary: Partial<Record<ServerName, ServerTodoSummaryItem[]>> = {};

    servers.forEach((serverName) => {
      const items = todos
        .filter((todo) => {
          // serverNames가 비어있으면 전체 서버에 표시
          const sn = todo.serverNames ?? [];
          return sn.length === 0 || sn.includes(serverName);
        })
        .map((todo) => {
          const state = stateMap[`${serverName}-${todo.todoId}`];
          const enabled =
            state?.enabled !== undefined ? state.enabled : todo.defaultEnabled;
          const checked = state?.checked ?? false;
          const visibleToday =
            todo.visibleWeekdays.length === 0 || todo.visibleWeekdays.includes(todayWeekday);

          if (!enabled || !visibleToday) {
            return null;
          }

          return {
            todoId: todo.todoId,
            serverName: serverName as ServerName,
            name: todo.contentName,
            checked,
            hasState: Boolean(state),
          } as ServerTodoSummaryItem;
        })
        .filter(Boolean) as ServerTodoSummaryItem[];

      if (items.length > 0) {
        summary[serverName] = items;
      }
    });

    return summary;
  }, [serverTodosOverview.data, servers, todayWeekday]);

  const handleSummaryToggle = async (item: ServerTodoSummaryItem) => {
    if (isServerTodoUpdating) {
      return;
    }

    if (!item.hasState) {
      try {
        await toggleServerTodoEnabledMutation.mutateAsync({
          todoId: item.todoId,
          serverName: item.serverName,
          enabled: true,
        });
      } catch {
        return;
      }
    }

    checkServerTodoMutation.mutate({
      todoId: item.todoId,
      serverName: item.serverName,
      checked: !item.checked,
    });
  };

  return (
    <WideDefaultLayout>
      <Dial />

      <TestDataNotify />

      <Wrapper $showWide={showWide} $count={characters.length}>
        {showSortForm && <SortCharacters characters={characters} />}
        {showGridForm && (
          <GridConfigPanel
            gridConfig={gridConfig}
            showWide={showWide}
            onConfigChange={setGridConfig}
          />
        )}

        {showDailyTodoSortForm && <DailyTodoSortModal />}

        <ViewModeToggleWrap>
          <ViewModeToggle role="tablist" aria-label="숙제 뷰 모드">
            <ViewModeButton
              type="button"
              role="tab"
              aria-selected={effectiveViewMode === "card"}
              $active={effectiveViewMode === "card"}
              onClick={() => setViewMode("card")}
            >
              카드 뷰
            </ViewModeButton>
            <ViewModeButton
              type="button"
              role="tab"
              aria-selected={effectiveViewMode === "table"}
              $active={effectiveViewMode === "table"}
              disabled={!isTableViewAllowed}
              title={
                isTableViewAllowed
                  ? "표 뷰로 전환"
                  : "가로 폭이 좁아 표 뷰를 사용할 수 없습니다"
              }
              onClick={() => setViewMode("table")}
            >
              표 뷰
              <BetaBadge>BETA</BetaBadge>
            </ViewModeButton>
          </ViewModeToggle>
        </ViewModeToggleWrap>

        <TopSection>
          {shouldShowAd && <Ad placementName="video" />}

          <LeftColumn>
            {characters.length > 0 && (
              <Profit
                characters={characters}
                onSummaryClick={() => setSummaryModal(true)}
              />
            )}

            {showServerButtons && (
              <ServerChipList>
                {[
                  {
                    key: "전체",
                    label: "전체",
                    count: visibleCharacters.length,
                    summary: undefined,
                  },
                  ...Object.entries<number>(serverCounts).map(
                    ([serverName, count]) => ({
                      key: serverName,
                      label: serverName,
                      count,
                      summary: serverTodoSummaries[serverName as ServerName],
                    })
                  ),
                ].map((item) => {
                  const isActive = todoServer === item.key;
                  const isAll = item.key === "전체";

                  return (
                    <ServerChip
                      key={item.key}
                      type="button"
                      aria-pressed={isActive}
                      $active={isActive}
                      onClick={() =>
                        setTodoServer(item.key as ServerName | "전체")
                      }
                    >
                      <ChipHeader>
                        <ChipTitle>
                          {item.label} {item.count}개
                        </ChipTitle>
                        {isAll && visibleServers.length > 0 && (
                          <ChipButton
                            variant="outlined"
                            onClick={(event) => {
                              event.stopPropagation();
                              setServerTodoModal(true);
                            }}
                          >
                            원정대 숙제 관리
                          </ChipButton>
                        )}
                      </ChipHeader>
                      {!isAll && item.summary && item.summary.length > 0 && (() => {
                        const isExpanded = expandedServers.has(item.key);
                        const visibleItems = isExpanded ? item.summary : item.summary.slice(0, 3);
                        const hiddenCount = item.summary.length - 3;

                        return (
                          <TodoSummary>
                            {visibleItems.map((summaryItem) => (
                              <TodoBadge
                                key={`${item.label}-${summaryItem.name}`}
                                $checked={summaryItem.checked}
                                $disabled={isServerTodoUpdating}
                                role="button"
                                tabIndex={isServerTodoUpdating ? -1 : 0}
                                aria-pressed={summaryItem.checked}
                                aria-disabled={isServerTodoUpdating}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (isServerTodoUpdating) return;
                                  handleSummaryToggle(summaryItem);
                                }}
                                onKeyDown={(event) => {
                                  if (isServerTodoUpdating) return;
                                  if (
                                    event.key === "Enter" ||
                                    event.key === " "
                                  ) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleSummaryToggle(summaryItem);
                                  }
                                }}
                              >
                                {summaryItem.name}
                              </TodoBadge>
                            ))}
                            {hiddenCount > 0 && (
                              <ToggleBadge
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setExpandedServers((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(item.key)) {
                                      next.delete(item.key);
                                    } else {
                                      next.add(item.key);
                                    }
                                    return next;
                                  });
                                }}
                              >
                                {isExpanded ? "접기" : `+${hiddenCount}개`}
                              </ToggleBadge>
                            )}
                          </TodoSummary>
                        );
                      })()}
                    </ServerChip>
                  );
                })}
              </ServerChipList>
            )}
          </LeftColumn>
        </TopSection>

        {effectiveViewMode === "table" ? (
          <TodoTable characters={characters} />
        ) : (
          <TodoList characters={characters} gridConfig={gridConfig} />
        )}
      </Wrapper>

      <UncheckedSummaryModal
        isOpen={summaryModal}
        onClose={() => setSummaryModal(false)}
        characters={characters}
      />

      {visibleServers.length > 0 && (
        <Modal
          title="원정대 공통 숙제"
          isOpen={!!serverTodoModal}
          onClose={() => setServerTodoModal(false)}
        >
          <ModalBody>
            {!!serverTodoModal && (
              <ServerTodos servers={visibleServers} showAllWeekdays />
            )}
          </ModalBody>
        </Modal>
      )}
    </WideDefaultLayout>
  );
};

export default TodoIndex;

const Wrapper = styled.div<{ $showWide: boolean; $count: number }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TopSection = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  width: 100%;

  ${({ theme }) => theme.medias.max768} {
    flex-direction: column-reverse;
  }
`;

const LeftColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  width: 100%;
`;

const ModalBody = styled.div`
  width: min(600px, 90vw);
  max-height: 70vh;
  overflow-y: auto;
`;

const ToggleBadge = styled.span`
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 6px;
  background: ${({ theme }) => theme.app.palette.purple[50]};
  color: ${({ theme }) => theme.app.palette.purple[300]};
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const TodoSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const TodoBadge = styled.span<{ $checked: boolean; $disabled: boolean }>`
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme, $checked }) =>
      $checked ? theme.app.palette.blue[350] : theme.app.border};
  color: ${({ theme, $checked }) =>
    $checked ? theme.app.palette.blue[350] : theme.app.text.gray1};
  background: ${({ theme, $checked }) =>
    $checked ? theme.app.palette.blue[50] : "transparent"};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition:
    background 0.2s ease,
    color 0.2s ease,
    opacity 0.2s ease;
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  user-select: none;
`;

const ServerChipList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px;
  width: 100%;
`;

const ServerChip = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.app.text.dark2 : theme.app.border};
  background: ${({ theme }) => theme.app.bg.white};
  cursor: pointer;
  transition:
    border 0.2s ease,
    background 0.2s ease;
  width: 100%;
`;

const ChipTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.app.text.dark2};
`;

const ChipHeader = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const ChipButton = styled(Button)`
  padding: 4px 8px;
  font-size: 12px;
`;

const ViewModeToggleWrap = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;

  /* 하단 네비로 전환되는 구간(모바일/태블릿)에서는 표 뷰 자체가 비활성이므로 토글 숨김 */
  ${({ theme }) => theme.medias.max1024} {
    display: none;
  }
`;

const ViewModeToggle = styled.div`
  display: inline-flex;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.app.bg.white};
  /* overflow: visible — BetaBadge가 토글 밖으로 튀어나올 수 있어야 함 */
`;

const ViewModeButton = styled.button<{ $active: boolean }>`
  position: relative;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $active, theme }) =>
    $active ? theme.app.bg.white : theme.app.text.dark2};
  background: ${({ $active, theme }) =>
    $active ? theme.app.text.dark2 : "transparent"};
  border: none;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, opacity 0.2s ease;

  &:first-child {
    border-top-left-radius: 7px;
    border-bottom-left-radius: 7px;
  }

  &:last-child {
    border-top-right-radius: 7px;
    border-bottom-right-radius: 7px;
  }

  & + & {
    border-left: 1px solid ${({ theme }) => theme.app.border};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

const BetaBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  padding: 1px 4px;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.4px;
  line-height: 1.2;
  color: #ffffff;
  background: #ff4747;
  border-radius: 3px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  pointer-events: none;
`;

